import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { decodeJwt, isTokenExpired } from '@/shared/lib/jwt';
import * as authApi from '../api/authApi';
import { AuthContext, type AuthContextValue } from './auth-context';
import { refreshSession } from './refreshSession';
import {
  clearSession,
  getSession,
  setSession,
  startCrossTabSync,
  subscribe,
} from './sessionStore';
import { isGlobalRole, type AuthStatus, type AuthUser, type GlobalRole } from './types';

export interface AuthProviderProps {
  children: ReactNode;
}

/** Odtwarza uzytkownika z zawartosci tokena dostepu. */
function readUserFromToken(accessToken: string | undefined): AuthUser | null {
  if (!accessToken) return null;

  const payload = decodeJwt(accessToken);
  if (!payload) return null;

  return {
    id: payload.sub,
    email: payload.email,
    roles: (payload.globalRoles ?? []).filter(isGlobalRole),
  };
}

/**
 * Udostepnia stan uwierzytelnienia calej aplikacji.
 *
 * Sesja zyje w magazynie poza Reactem (`sessionStore`), bo korzysta z niej takze
 * klient HTTP. Tutaj tylko ja subskrybujemy i wystawiamy w wygodnej formie.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const session = useSyncExternalStore(subscribe, getSession, getSession);
  const [bootstrapped, setBootstrapped] = useState(false);

  // Start aplikacji: jesli zapisany token juz wygasl, probujemy go odnowic,
  // zanim zdecydujemy, czy uzytkownik jest zalogowany.
  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      const stored = getSession();
      if (stored && isTokenExpired(stored.accessToken)) {
        await refreshSession();
      }
      if (!cancelled) {
        setBootstrapped(true);
      }
    };

    void bootstrap();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => startCrossTabSync(), []);

  const login = useCallback(async (email: string, password: string, rememberMe: boolean) => {
    const response = await authApi.login({ email, password });

    setSession({
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
      sessionId: response.sessionId,
      persistent: rememberMe,
    });
  }, []);

  const logout = useCallback(async () => {
    const stored = getSession();

    if (stored) {
      try {
        await authApi.logout(stored.refreshToken);
      } catch {
        // Nieudane wylogowanie po stronie serwera nie moze zablokowac
        // wylogowania lokalnego — token i tak wygasnie.
      }
    }

    clearSession();
  }, []);

  // Wyciagniete do zmiennej, zeby zaleznosci `useMemo` byly dokladnie tym,
  // czego uzywa cialo funkcji — inaczej React Compiler pomija optymalizacje.
  const accessToken = session?.accessToken;

  const user = useMemo(() => readUserFromToken(accessToken), [accessToken]);

  const accessTokenExpiresAt = useMemo(() => {
    const exp = accessToken ? decodeJwt(accessToken)?.exp : undefined;
    return exp ? new Date(exp * 1000) : null;
  }, [accessToken]);

  const hasRole = useCallback(
    (...roles: GlobalRole[]) => {
      if (!user) return false;
      if (user.roles.includes('ADMIN')) return true;
      return roles.some((role) => user.roles.includes(role));
    },
    [user],
  );

  const status: AuthStatus = !bootstrapped ? 'loading' : session ? 'authenticated' : 'unauthenticated';

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      isAuthenticated: status === 'authenticated',
      sessionId: session?.sessionId ?? null,
      accessTokenExpiresAt,
      login,
      logout,
      hasRole,
    }),
    [status, user, session?.sessionId, accessTokenExpiresAt, login, logout, hasRole],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
